/**
 * BankAccountFormDialog - Dialog for creating/editing bank accounts
 */

import type {
  AccountType,
  BankAccountPublic,
  CreateBankAccountRequest,
  UpdateBankAccountRequest,
} from '@garden-platform/shared';
import type * as React from 'react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useCreateBankAccount, useUpdateBankAccount } from '../../hooks';

interface FormData {
  bankName: string;
  accountName: string;
  accountNumber: string;
  routingNumber: string;
  accountType: AccountType;
  isActive: boolean;
}

const defaultFormData: FormData = {
  bankName: '',
  accountName: '',
  accountNumber: '',
  routingNumber: '',
  accountType: 'checking',
  isActive: true,
};

export interface BankAccountFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bankAccount?: BankAccountPublic;
}

export const BankAccountFormDialog: React.FC<BankAccountFormDialogProps> = ({
  open,
  onOpenChange,
  bankAccount,
}) => {
  const isEditing = !!bankAccount;
  const createMutation = useCreateBankAccount();
  const updateMutation = useUpdateBankAccount();

  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  // Reset form when dialog opens/closes or bank account changes
  useEffect(() => {
    if (open) {
      if (bankAccount) {
        setFormData({
          bankName: bankAccount.bankName,
          accountName: bankAccount.accountName || '',
          accountNumber: '', // Don't populate - it's encrypted
          routingNumber: bankAccount.routingNumber,
          accountType: bankAccount.accountType,
          isActive: bankAccount.isActive,
        });
      } else {
        setFormData(defaultFormData);
      }
      setErrors({});
    }
  }, [open, bankAccount]);

  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.bankName.trim()) {
      newErrors.bankName = 'Bank name is required';
    }

    if (!formData.accountName.trim()) {
      newErrors.accountName = 'Account name is required';
    }

    if (!isEditing && !formData.accountNumber.trim()) {
      newErrors.accountNumber = 'Account number is required';
    }

    if (!formData.routingNumber.trim()) {
      newErrors.routingNumber = 'Routing number is required';
    } else if (!/^\d{9}$/.test(formData.routingNumber)) {
      newErrors.routingNumber = 'Routing number must be 9 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    if (isEditing && bankAccount) {
      const updateData: UpdateBankAccountRequest = {
        bankName: formData.bankName,
        accountName: formData.accountName || undefined,
        routingNumber: formData.routingNumber,
        accountType: formData.accountType,
        isActive: formData.isActive,
      };

      // Only include account number if it was changed
      if (formData.accountNumber) {
        updateData.accountNumber = formData.accountNumber;
      }

      await updateMutation.mutateAsync({ id: bankAccount.id, data: updateData });
    } else {
      const createData: CreateBankAccountRequest = {
        bankName: formData.bankName,
        accountName: formData.accountName,
        accountNumber: formData.accountNumber,
        routingNumber: formData.routingNumber,
        accountType: formData.accountType,
      };

      await createMutation.mutateAsync(createData);
    }

    onOpenChange(false);
  };

  const getInputClassName = (field: keyof FormData) =>
    cn(errors[field] && 'border-red-500 focus:border-red-500 focus:ring-red-500');

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Bank Account' : 'Add Bank Account'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the bank account details. Leave account number blank to keep the existing one.'
              : 'Enter the bank account details. The account number will be encrypted.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Bank Name */}
          <div className="space-y-1.5">
            <Label htmlFor="bankName">
              Bank Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="bankName"
              type="text"
              value={formData.bankName}
              onChange={(e) => handleChange('bankName', e.target.value)}
              placeholder="Chase Bank"
              className={getInputClassName('bankName')}
            />
            {errors.bankName && <p className="text-sm text-red-600">{errors.bankName}</p>}
          </div>

          {/* Account Name */}
          <div className="space-y-1.5">
            <Label htmlFor="accountName">
              Account Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="accountName"
              type="text"
              value={formData.accountName}
              onChange={(e) => handleChange('accountName', e.target.value)}
              placeholder="Operating Account"
              className={getInputClassName('accountName')}
            />
            {errors.accountName && <p className="text-sm text-red-600">{errors.accountName}</p>}
          </div>

          {/* Account Number */}
          <div className="space-y-1.5">
            <Label htmlFor="accountNumber">
              Account Number {!isEditing && <span className="text-red-500">*</span>}
              {isEditing && (
                <span className="text-muted-foreground text-xs">
                  (leave blank to keep existing)
                </span>
              )}
            </Label>
            <Input
              id="accountNumber"
              type="text"
              value={formData.accountNumber}
              onChange={(e) => handleChange('accountNumber', e.target.value)}
              placeholder={isEditing ? `****${bankAccount?.accountNumberLastFour || ''}` : ''}
              className={getInputClassName('accountNumber')}
            />
            {errors.accountNumber && <p className="text-sm text-red-600">{errors.accountNumber}</p>}
          </div>

          {/* Routing Number */}
          <div className="space-y-1.5">
            <Label htmlFor="routingNumber">
              Routing Number <span className="text-red-500">*</span>
            </Label>
            <Input
              id="routingNumber"
              type="text"
              value={formData.routingNumber}
              onChange={(e) => handleChange('routingNumber', e.target.value)}
              placeholder="123456789"
              maxLength={9}
              className={getInputClassName('routingNumber')}
            />
            {errors.routingNumber && <p className="text-sm text-red-600">{errors.routingNumber}</p>}
          </div>

          {/* Account Type */}
          <div className="space-y-1.5">
            <Label htmlFor="accountType">Account Type</Label>
            <Select
              value={formData.accountType}
              onValueChange={(v) => handleChange('accountType', v as AccountType)}
            >
              <SelectTrigger id="accountType">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="checking">Checking</SelectItem>
                <SelectItem value="savings">Savings</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Active Status (only for editing) */}
          {isEditing && (
            <div className="space-y-1.5">
              <Label htmlFor="isActive">Status</Label>
              <Select
                value={formData.isActive ? 'active' : 'inactive'}
                onValueChange={(v) => handleChange('isActive', v === 'active')}
              >
                <SelectTrigger id="isActive">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Account'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

BankAccountFormDialog.displayName = 'BankAccountFormDialog';
